import {
  ALERT_MANAGER_EVENT_TYPE_MODAL,
  ALERT_MANAGER_EVENT_TYPE_MODAL_WITH_PROMISE,
  INSTANCE_DEFAULT,
} from '../../utils/spds/constants/alert-manager';
import { assert } from '@ember/debug';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

/**
 * SPDS Modal Service
 *
 * Provides a clean API for creating and managing modals through the alert-manager event bus.
 * This service communicates with the SpdsModalContainerGlobal component via alert-manager broadcasts.
 *
 * @example
 * // Create a simple modal
 * const modalId = await this.spdsModal.create({
 *   modalComponent: 'transactional',
 *   title: 'Confirm Action',
 *   bodyText: 'Are you sure?',
 *   primaryBtnText: 'Yes',
 *   secondaryBtnText: 'No'
 * });
 *
 * @example
 * // Create a modal with promise-based flow control
 * try {
 *   const modalId = await this.spdsModal.createWithPromise({
 *     modalComponent: 'destructive',
 *     title: 'Delete Item',
 *     bodyText: 'This action cannot be undone',
 *     primaryBtnText: 'Delete',
 *     secondaryBtnText: 'Cancel'
 *   });
 *   // Primary button was clicked
 *   console.log('User confirmed deletion');
 * } catch (modalId) {
 *   // Secondary button was clicked or modal was closed
 *   console.log('User cancelled');
 * }
 */
export default class SpdsModalService extends Service {
  @service alertManager;

  /** @private */
  #openStack = [];

  @tracked openModalStack = [];

  get topDownStack() {
    return [...this.openModalStack].reverse();
  }

  /**
   * The dialog element of the highest modal in the stack that is open, walking down from the top.
   * A modal counts as open through its exit transition, until its close is reported.
   */
  get topmostOpenModal() {
    for (let modal of this.topDownStack) {
      if (modal.isOpen) return modal.baseElement ?? null;
    }

    return null;
  }

  /**
   * Resolves a modal in the stack by its component id.
   *
   * @param {string} id - The component id (`Spds::Core::Base.id`)
   * @returns {Object|null} - The modal component instance, or null when none is registered
   */
  openModalFor = id => {
    return this.topDownStack.find(modal => modal.id === id) ?? null;
  };

  /**
   * Called from `spds/core/modal` when a modal dialog is shown with `showModal()`.
   * Idempotent for the same instance (moves it to the top if already present).
   *
   * @param {Object} modal - The `spds/core/modal` component instance
   */
  registerOpenModal = modal => {
    assert(
      'spds/modal registerOpenModal expects a modal component instance, not an id string',
      typeof modal !== 'string'
    );

    this.#openStack = this.#openStack.filter(m => m !== modal);
    this.#openStack.push(modal);
    this.openModalStack = [...this.#openStack];
  };

  /**
   * Called from `spds/core/modal` when its dialog closes, and again from `willDestroy`.
   *
   * @param {Object} modal - The `spds/core/modal` component instance
   */
  unregisterOpenModal = modal => {
    this.#openStack = this.#openStack.filter(m => m !== modal);
    this.openModalStack = [...this.#openStack];
  };

  /**
   * Closes a modal by its ID
   * @param {string} id - The modal ID to close (if not provided, closes the last modal)
   * @returns {Promise<boolean>} - Returns true if modal was closed, false if onBeforeClose prevented closure
   */
  close = async id => {
    return await this.alertManager.clear(ALERT_MANAGER_EVENT_TYPE_MODAL, INSTANCE_DEFAULT, id);
  };

  /**
   * Creates a confirmation modal with promise-based flow control
   * Returns true if user confirms (primary button), false if user cancels (secondary button or closes modal)
   * Automatically closes the modal after user interaction.
   *
   * @param {Object} [data={}] - Modal configuration (merged with default confirm settings)
   * @param {string} [data.title='Please confirm your choice'] - Modal title
   * @param {string} [data.bodyText='Are you sure you want to proceed?'] - Modal body text
   * @param {string} [data.primaryBtnText='Confirm'] - Primary button text
   * @param {string} [data.secondaryBtnText='Cancel'] - Secondary button text
   * @param {string} [data.modalComponent='transactional'] - Modal type
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   *
   * @example
   * const confirmed = await this.spdsModal.confirm({
   *   title: 'Delete Item',
   *   bodyText: 'Are you sure you want to delete this item?'
   * });
   * if (confirmed) {
   *   await this.deleteItem();
   * }
   */
  confirm = async (data = {}) => {
    let modalId, result;

    try {
      modalId = await this.createWithPromise({
        bodyText: 'Are you sure you want to proceed?',
        modalComponent: 'transactional',
        primaryBtnText: 'Confirm',
        secondaryBtnText: 'Cancel',
        title: 'Please confirm your choice',
        ...data,
      });

      result = true;
    } catch (id) {
      modalId = id;
      result = false;
    } finally {
      await this.close(modalId);
    }

    return result;
  };

  /**
   * Creates a modal and returns its ID
   * @param {Object} data - Modal configuration based on baseModal structure
   * @param {string|Object} data.modalComponent - Modal type ('acknowledgement', 'announcement', 'destructive', 'transactional', 'standard') or custom component
   * @param {string} data.title - Modal title
   * @param {string} data.bodyText - Modal body text (html-safe for standard modals)
   * @param {boolean} [data.closeOnClickOutside=false] - Whether clicking outside closes the modal
   * @param {Function} [data.onClose] - Callback when modal closes (receives modalId)
   * @param {Function} [data.onBeforeClose] - Async callback before close (can prevent closure by returning false)
   * @param {Function} [data.primaryBtnAction] - Primary button click handler (receives modalId)
   * @param {string} [data.primaryBtnText='OK'] - Primary button text
   * @param {Function} [data.secondaryBtnAction] - Secondary button click handler (receives modalId)
   * @param {string} [data.secondaryBtnText] - Secondary button text
   * @returns {Promise<string>} - The generated modal ID
   */
  create = data => {
    return this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_MODAL, data);
  };

  /**
   * Creates a modal and returns a promise that resolves/rejects based on button actions
   * This provides a promise-based flow control for modal interactions.
   *
   * The promise:
   * - Resolves with modal ID when primary button is clicked
   * - Rejects with modal ID when secondary button is clicked or modal is closed
   *
   * @param {Object} data - Modal configuration based on baseModal structure (same as create)
   * @returns {Promise<string>} - Promise that resolves with modal ID on primary button click,
   *                               rejects with modal ID on secondary button click or close
   */
  createWithPromise = data => {
    return this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_MODAL_WITH_PROMISE, data);
  };

  /**
   * Creates a destructive confirmation modal (red warning styling)
   * Convenience method that calls confirm() with modalComponent set to 'destructive'.
   * Use for dangerous actions like deleting data.
   *
   * @param {Object} [data={}] - Modal configuration (same as confirm, but modalComponent defaults to 'destructive')
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   *
   * @example
   * const confirmed = await this.spdsModal.destructiveConfirm({
   *   title: 'Delete Account',
   *   bodyText: 'This action cannot be undone. All data will be permanently deleted.'
   * });
   * if (confirmed) {
   *   await this.deleteAccount();
   * }
   */
  destructiveConfirm = async (data = {}) => {
    return await this.confirm({ modalComponent: 'destructive', ...data });
  };

  /**
   * Updates an already-open modal in place, merging the provided params into
   * the existing modal configuration. Broadcasts the `update` variant of the
   * modal event so the container patches the matching modal rather than
   * creating a new one.
   *
   * @param {string} modalId - The ID of the modal to update
   * @param {Object} modalParams - Partial modal configuration to merge in
   * @returns {Promise<*>} - Resolves once the container has handled the update
   */
  update = (modalId, modalParams) => {
    return this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_MODAL, {
      ...modalParams,
      event: 'update',
      id: modalId,
    });
  };
}
